import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, Card, Typography, Spin, Alert, Divider, Modal } from 'antd';
import { UserOutlined, LockOutlined, SafetyCertificateOutlined, BookOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import useValidateClientInstitute from '../api/ValidateClientInstitute';
import { setAuthParamsAction } from '../redux/authParams/actionCreators';
import './AuthPage.css';

const { Title, Text, Paragraph } = Typography;

/**
 * Student Authentication page for signin/signup
 */
const StudentAuth = () => {
  const { user, isAuthenticated, isLoading, error, login, register, clearError, setError } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { validateClientInstitute, isLoading: isValidating } = useValidateClientInstitute();

  // Redirect if already authenticated - go directly to dashboard
  useEffect(() => {
    if (isAuthenticated && user) {
      const nextUrl = new URLSearchParams(location.search).get('next');
      
      // If there's a specific next URL, use it
      if (nextUrl) {
        history.replace(nextUrl);
        return;
      }
      
      // For authenticated users, go directly to admin dashboard
      console.log('User is authenticated - redirecting to admin dashboard');
      history.replace('/admin');
    }
  }, [isAuthenticated, user, history, location]);

  // Handle callback errors
  useEffect(() => {
    const error = new URLSearchParams(location.search).get('error');
    if (error) {
      let errorMessage = 'Authentication failed. Please try again.';
      
      switch (error) {
        case 'missing_tokens':
          errorMessage = 'Authentication tokens were not received. Please try again.';
          break;
        case 'session_validation_failed':
          errorMessage = 'Session validation failed. Please try again.';
          break;
        case 'session_validation_error':
          errorMessage = 'Error validating your session. Please try again.';
          break;
        case 'callback_error':
          errorMessage = 'Error during authentication. Please try again.';
          break;
        default:
          errorMessage = `Authentication error: ${error}. Please try again.`;
      }
      
      // Set the error in the auth context
      clearError(); // Clear any existing error first
      setTimeout(() => {
        // Use setTimeout to ensure the error is set after the component mounts
        setError(errorMessage);
      }, 100);
    }
  }, [location, clearError, setError]);

  // Validate client_id and institute_id before proceeding
  const validateAndProceed = async (action) => {
    const params = new URLSearchParams(location.search);
    const clientId = params.get('client_id');
    const instituteId = params.get('institute_id');

    // Check if both parameters are present
    if (!clientId || !instituteId) {
      Modal.error({
        title: 'Invalid URL',
        content: 'Missing required parameters. Please ensure client_id and institute_id are present in the URL.',
        okText: 'OK'
      });
      return;
    }

    try {
      // Validate client_id and institute_id
      console.log('Starting validation for:', { clientId, instituteId });
      await validateClientInstitute(clientId, instituteId);
      console.log('Validation successful, storing params and proceeding');
      
      // Store validated parameters in Redux store
      dispatch(setAuthParamsAction(clientId, instituteId));
      
      // If validation succeeds, proceed with the action
      clearError();
      if (action === 'login') {
        console.log('Calling login with student role');
        login('student');
      } else if (action === 'register') {
        console.log('Calling register with student role');
        register('student');
      }
    } catch (err) {
      console.error('Validation error caught in StudentAuth:', err);
      console.error('Error message:', err.message);
      // Show error popup if validation fails
      Modal.error({
        title: 'Invalid URL',
        content: err.message || 'Invalid url',
        okText: 'OK'
      });
    }
  };

  // Handle OIDC login with student role
  const handleOIDCLogin = () => {
    validateAndProceed('login');
  };

  // Handle OIDC register with student role
  const handleOIDCRegister = () => {
    validateAndProceed('register');
  };

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spin size="large" />
        <div className="auth-loading-text">
          <Text>Verifying your session...</Text>
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (error) {
    return (
      <div className="auth-container">
        <Card className="auth-card">
          <div className="auth-content">
            <div className="auth-header">
              <SafetyCertificateOutlined className="auth-icon error" />
              <Title level={2} className="auth-title">
                Authentication Error
              </Title>
            </div>
            
            <Alert
              message="Login Failed"
              description={error}
              type="error"
              showIcon
              className="auth-error"
            />
            
            <Button 
              type="primary" 
              size="large" 
              onClick={handleOIDCLogin}
              className="auth-button"
              icon={<UserOutlined />}
            >
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Main OIDC signin/signup page
  return (
    <div className="auth-container">
      <Card className="auth-card">
        <div className="auth-content">
          <div className="auth-header">
            <BookOutlined className="auth-icon student-icon" />
            <Title level={1} className="auth-title">
              Welcome Back
            </Title>
            <Text className="auth-subtitle">
              Sign in to access your dashboard
            </Text>
          </div>

          <div className="auth-actions">
            <Button 
              type="primary" 
              size="large" 
              onClick={handleOIDCLogin}
              className="auth-button oidc-button"
              icon={<LockOutlined />}
              loading={isLoading || isValidating}
            >
              Sign in with OIDC
            </Button>
            
            <Divider>or</Divider>
            
            <Button 
              type="default" 
              size="large" 
              onClick={handleOIDCRegister}
              className="auth-button signup-button"
              icon={<UserOutlined />}
              loading={isLoading || isValidating}
            >
              Create New Account
            </Button>
          </div>

          <div className="auth-info-section">
            <Paragraph className="auth-info">
              <SafetyCertificateOutlined /> Secure authentication powered by Stride ID
            </Paragraph>
            <Paragraph className="auth-info">
              You will be redirected to your identity provider for secure sign-in
            </Paragraph>
          </div>

          <div className="auth-footer">
            <Text type="secondary">
              Having trouble? Contact your administrator
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default StudentAuth;

